import { useEffect, useMemo, useState } from 'react'

import { ChartIndicators } from '@/components/ChartIndicators'
import { ChartLevels, type Level } from '@/components/ChartLevels'
import { PriceChart, type ChartApi } from '@/components/PriceChart'
import { StaleQuotes } from '@/components/StaleQuotes'
import { Notice, Segmented } from '@/components/ui'
import { useIndicators } from '@/hooks/useIndicators'
import { useLocalState } from '@/hooks/useLocalState'
import { useMarket } from '@/hooks/useMarket'
import { api } from '@/lib/api'
import { foldQuote } from '@/lib/liveCandles'
import { CANDLE_COUNT, TIMEFRAMES, type Candle, type Instrument, type Position, type Timeframe } from '@/lib/types'

/**
 * Modification du stop et de la cible d'une position ouverte, sur le graphique.
 *
 * **L'écran montre la vue stratégie, l'inverse de ce que détient le
 * courtier** — c'est le choix explicite de l'utilisateur, le même que dans
 * l'onglet Positions : sans cette inversion, l'application ne dirait rien de
 * plus que la plateforme du courtier.
 *
 * Concrètement, sur une position que le courtier détient à la vente :
 *
 * | Niveau               | Chez le courtier | Ici          | Compte réel |
 * |----------------------|------------------|--------------|-------------|
 * | au-dessus de l'entrée| stop             | **ma cible** | il perd     |
 * | en dessous           | cible            | **mon stop** | il gagne    |
 *
 * L'état interne, lui, reste exprimé en niveaux DU COURTIER. C'est ce qui part
 * dans la requête : traduire au dernier moment, à un seul endroit, évite
 * d'envoyer un stop là où il fallait une cible.
 *
 * Les distances se manipulent en pips depuis le **prix d'entrée** de la
 * position, puis sont reconverties en prix absolus pour le courtier.
 */
export function PositionEditor({
  position,
  instrument,
  onClose,
  onSaved,
}: {
  position: Position
  instrument: Instrument | undefined
  onClose: () => void
  onSaved: () => void
}) {
  const [timeframe, setTimeframe] = useLocalState<Timeframe>('timeframe.position', 'M15')
  // Les indicateurs sont un réglage global : ils suivent jusqu'ici. Régler un
  // stop sans les moyennes qu'on vient de poser sur le graphique principal
  // reviendrait à travailler sur deux cartes différentes.
  const [indicators] = useIndicators()
  const watched = useMemo(() => [position.symbol], [position.symbol])
  const [candles, setCandles] = useState<Candle[]>([])
  const [chartApi, setChartApi] = useState<ChartApi | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const digits = instrument?.digits ?? 5
  const pipSize = Number(instrument?.pip_size ?? 0.0001)
  const entry = Number(position.entry_price)
  // Placement des lignes : chez le courtier, une vente a son stop au-dessus du
  // prix d'entrée et sa cible en dessous. Le placement suit donc TOUJOURS le
  // sens du courtier, même si les étiquettes, elles, parlent stratégie.
  const isSell = position.broker_side === 'SELL'
  // Le sens tel que l'utilisateur l'a saisi : l'inverse de celui du courtier.
  const monSens = position.entry_side

  const toPips = (price: string | null) =>
    price === null ? null : Math.abs(Number(price) - entry) / pipSize

  const [stopPips, setStopPips] = useState<number | null>(toPips(position.stop_loss))
  const [targetPips, setTargetPips] = useState<number | null>(toPips(position.take_profit))

  /*
   * Chiffrage en devise du compte.
   *
   * Le serveur envoie la valeur d'un pip POUR CE VOLUME une seule fois, avec la
   * position. L'écran multiplie : pas un aller-retour par image pendant qu'on
   * fait glisser une ligne. Quand la valeur manque — cotation de conversion
   * indisponible — on affiche « — » plutôt qu'un montant inventé, et le
   * garde-fou refuse d'éloigner le stop tant qu'il ne peut pas le mesurer.
   */
  const pipValue = position.pip_value === null ? null : Number(position.pip_value)
  const balance = position.account_balance === null ? null : Number(position.account_balance)
  const limitPercent = Number(position.max_risk_percent)
  const currency = position.account_currency

  const money = (pips: number | null) =>
    pips === null || pipValue === null ? null : pips * pipValue
  const percentOf = (amount: number | null) =>
    amount === null || balance === null || balance <= 0 ? null : (amount / balance) * 100
  const format = (value: number) =>
    value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const stopMoney = money(stopPips)
  const targetMoney = money(targetPips)
  const stopPercent = percentOf(stopMoney)

  const currentStopPips = toPips(position.stop_loss)
  // Rapprocher le stop du prix d'entrée réduit toujours le risque : autorisé
  // même au-delà de la limite, sinon une position déjà trop exposée
  // deviendrait impossible à corriger. Même règle que sur le serveur.
  const movingCloser =
    stopPips !== null && (currentStopPips === null || stopPips <= currentStopPips)

  /** Pourquoi le déplacement serait refusé — `null` si tout va bien. */
  const refusal = (() => {
    if (movingCloser) return null
    if (stopPips === null) {
      return position.stop_loss
        ? 'Retirer ce niveau laisserait la position sans perte maximale sur le compte réel : refusé.'
        : null
    }
    if (stopPercent === null) {
      return `Exposition non chiffrable en ${currency} (cotation de conversion manquante) : éloigner ce niveau est refusé tant qu'on ne peut pas la mesurer.`
    }
    if (stopPercent > limitPercent) {
      return `Ce niveau est le stop chez le courtier : l'atteindre coûterait ${format(stopMoney ?? 0)} ${currency} au compte réel, soit ${stopPercent.toFixed(2)} % du capital — au-dessus de la limite de ${limitPercent} %.`
    }
    return null
  })()

  useEffect(() => {
    let cancelled = false
    const load = (initial: boolean) =>
      api
        .trendbars(position.symbol, timeframe, CANDLE_COUNT[timeframe])
        .then((data) => !cancelled && setCandles(data))
        .catch((err: unknown) => {
          // Un rafraîchissement de fond raté garde le graphique tel quel.
          if (cancelled || !initial) return
          setError(err instanceof Error ? err.message : 'Chandeliers indisponibles')
        })
    void load(true)

    // Mêmes raisons que sur le graphique principal : combler ce que les
    // cotations n'ont pas pu voir pendant une mise en veille.
    const refresh = () => {
      if (document.visibilityState === 'visible') void load(false)
    }
    const timer = window.setInterval(refresh, 5 * 60_000)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [position.symbol, timeframe])

  /*
   * La dernière bougie suit le marché ici aussi.
   *
   * Le prix d'ENTRÉE, lui, reste celui de la position : c'est là qu'elle a
   * été ouverte, il n'a pas à bouger. Mais sans ce repli, la dernière bougie
   * restait sur sa clôture du chargement pendant qu'on réglait ses niveaux —
   * on plaçait un stop par rapport à un prix qui n'était plus le bon.
   */
  const market = useMarket(watched)
  const liveQuote = market.quotes[position.symbol]
  useEffect(() => {
    if (!liveQuote) return
    const price = Number(liveQuote.mid)
    setCandles((current) => foldQuote(current, price, timeframe, Date.now() / 1000))
  }, [liveQuote, timeframe])

  const priceOf = (pips: number | null, isStop: boolean) => {
    if (pips === null) return null
    const above = isStop ? isSell : !isSell
    return above ? entry + pips * pipSize : entry - pips * pipSize
  }

  const levels = useMemo<Level[]>(() => {
    const result: Level[] = []
    const stopPrice = priceOf(stopPips, true)
    const targetPrice = priceOf(targetPips, false)

    // Le niveau que le courtier appelle « stop » est MA CIBLE : si le prix
    // l'atteint, ma thèse était bonne. Le compte réel, lui, y perd — d'où la
    // mention du courtier entre parenthèses, et la limite de risque qui porte
    // sur cette ligne-là.
    if (stopPrice !== null) {
      result.push({
        id: 'stop',
        price: stopPrice,
        pips: Math.round((stopPips ?? 0) * 10) / 10,
        label: 'MA CIBLE',
        mirrorLabel: 'stop du courtier',
        // Vert : c'est un gain dans la vue stratégie. Orange dès que la limite
        // du compte réel est franchie — la ligne dit qu'on est allé trop loin
        // avant même qu'on lise le texte.
        color: stopPercent !== null && stopPercent > limitPercent ? '#f5a623' : '#26a69a',
        amount:
          stopMoney === null
            ? null
            : `+${format(stopMoney)} ${currency}` +
              (stopPercent === null ? '' : ` · ${stopPercent.toFixed(2)} % du compte réel`),
        isRealStop: true,
      })
    }
    if (targetPrice !== null) {
      result.push({
        id: 'target',
        price: targetPrice,
        pips: Math.round((targetPips ?? 0) * 10) / 10,
        label: 'MON STOP',
        mirrorLabel: 'cible du courtier',
        color: '#ef5350',
        amount: targetMoney === null ? null : `−${format(targetMoney)} ${currency}`,
        isRealStop: false,
      })
    }
    return result
  }, [stopPips, targetPips, entry, pipSize, isSell, stopMoney, targetMoney, stopPercent])

  const stopPrice = priceOf(stopPips, true)
  const targetPrice = priceOf(targetPips, false)
  const changed =
    stopPrice?.toFixed(digits) !== (position.stop_loss ? Number(position.stop_loss).toFixed(digits) : undefined) ||
    targetPrice?.toFixed(digits) !== (position.take_profit ? Number(position.take_profit).toFixed(digits) : undefined)

  async function apply() {
    setBusy(true)
    setError(null)
    try {
      await api.amendPosition(position.position_id, {
        stop_loss: stopPrice !== null ? stopPrice.toFixed(digits) : undefined,
        take_profit: targetPrice !== null ? targetPrice.toFixed(digits) : undefined,
      })
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Modification refusée')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="safe-top safe-bottom fixed inset-0 z-50 flex flex-col bg-base">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div>
          <p className="num text-[15px] font-semibold text-ink-strong">
            <span className={monSens === 'BUY' ? 'text-up' : 'text-down'}>
              {monSens === 'BUY' ? 'ACHAT' : 'VENTE'}
            </span>{' '}
            {position.symbol} {position.lots} lot
          </p>
          <p className="num text-[11px] text-ink-muted">
            entrée {position.entry_price}
            {position.current_price && ` → ${position.current_price}`}
            {' · '}
            {/* Le sens réel reste écrit : la vue stratégie ne doit jamais faire
                oublier ce que le courtier détient vraiment (§2). */}
            <span className={isSell ? 'text-down' : 'text-up'}>
              courtier : {position.broker_side} {position.lots} lot
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="min-h-touch w-11 text-[16px] text-ink-muted"
        >
          ✕
        </button>
      </div>

      <div className="border-b border-border px-3 py-2">
        <Segmented<Timeframe>
          value={timeframe}
          onChange={setTimeframe}
          options={TIMEFRAMES.map((item) => ({ value: item, label: item }))}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/*
          Les niveaux montrés ici sont ceux de la position RÉELLE : aucune
          inversion. Le sens affiché est celui du courtier.
        */}
        <StaleQuotes age={market.quoteAge} />

        <PriceChart
          candles={candles}
          height={Math.max(window.innerHeight - 320, 240)}
          onReady={setChartApi}
          includePrices={[entry, ...levels.map((l) => l.price)]}
        >
          <ChartLevels
            api={chartApi}
            entryPrice={entry}
            levels={levels}
            pipSize={pipSize}
            digits={digits}
            height={Math.max(window.innerHeight - 320, 240)}
            direction={
              monSens === 'BUY'
                ? 'ACHAT — mon stop est en dessous'
                : 'VENTE — mon stop est au-dessus'
            }
            onDrag={(id, pips) => (id === 'stop' ? setStopPips(pips) : setTargetPips(pips))}
          />
          <ChartIndicators api={chartApi} candles={candles} indicators={indicators} />
        </PriceChart>

        <div className="p-3">
          {error && <Notice tone="error" title="Modification" items={[error]} />}

          {refusal && <Notice tone="error" title="Au-delà de la limite" items={[refusal]} />}

          <dl className="mb-3 space-y-1 text-[13px]">
            <Row
              label="Ma cible"
              value={stopPrice !== null ? stopPrice.toFixed(digits) : '—'}
              amount={stopMoney === null ? null : `+${format(stopMoney)} ${currency}`}
              tone="up"
              hint={position.stop_loss ? `stop du courtier · avant : ${position.stop_loss}` : undefined}
            />
            <Row
              label="Mon stop"
              value={targetPrice !== null ? targetPrice.toFixed(digits) : '—'}
              amount={targetMoney === null ? null : `−${format(targetMoney)} ${currency}`}
              tone="down"
              hint={
                position.take_profit
                  ? `cible du courtier · avant : ${position.take_profit}`
                  : undefined
              }
            />
          </dl>

          {/* La limite est affichée en permanence : on doit savoir de combien
              on dispose AVANT de pousser la ligne, pas après un refus. */}
          <p className="mb-3 text-[11px] leading-relaxed text-ink-muted">
            Limite par position : <span className="num text-ink">{limitPercent} %</span> du
            capital
            {position.max_risk_amount && (
              <>
                {' '}
                = <span className="num text-ink">
                  {format(Number(position.max_risk_amount))} {currency}
                </span>
              </>
            )}
            {pipValue !== null && (
              <>
                {' '}· 1 pip ={' '}
                <span className="num text-ink">
                  {format(pipValue)} {currency}
                </span>{' '}
                pour {position.lots} lot
              </>
            )}
. Elle porte sur <span className="text-ink">ma cible</span> — c'est le
            stop du courtier, donc le seul niveau où le compte réel peut perdre.
            Elle est aussi vérifiée par le serveur : l'interface seule ne
            protégerait de rien.
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={apply}
              disabled={busy || !changed || refusal !== null}
              className="min-h-touch flex-1 rounded-md bg-accent text-[14px] font-semibold text-white disabled:opacity-40"
            >
              {busy
                ? 'Envoi…'
                : refusal !== null
                  ? `Au-delà de ${limitPercent} %`
                  : changed
                    ? 'Appliquer chez le courtier'
                    : 'Aucun changement'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="min-h-touch flex-1 rounded-md border border-border bg-elevated text-[14px] text-ink-muted"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  amount,
  tone,
  hint,
}: {
  label: string
  value: string
  /** Le même niveau exprimé en argent — la seule unité qui parle vraiment. */
  amount?: string | null
  tone?: 'up' | 'down'
  hint?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right">
        <span className="num text-ink">{value}</span>
        {amount && (
          <span
            className={`num ml-2 font-semibold ${tone === 'up' ? 'text-up' : 'text-down'}`}
          >
            {amount}
          </span>
        )}
        {hint && <span className="num block text-[10px] text-ink-muted">{hint}</span>}
      </dd>
    </div>
  )
}
